import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ehNativo } from 'services/plataforma';
import { fecharOverlayDoTopo } from 'services/overlays';

/**
 * O botão voltar de hardware do Android.
 *
 * <h2>O que o Capacitor já faz sozinho</h2>
 * Sem nenhum listener, ele volta no histórico da WebView e encerra o app quando não há mais para
 * onde voltar. Ou seja: a navegação entre telas já funcionava, e o problema NÃO era "o botão fecha o
 * app" — isso só acontece na raiz, que é o comportamento esperado no Android.
 *
 * <h2>Os dois problemas reais</h2>
 * O primeiro: **overlay aberto não intercepta o voltar**. Com um bottom sheet na tela, o botão
 * navegava para fora da página inteira em vez de fechar o sheet — que é o que o mesmo botão faz em
 * qualquer app Android, e o que o Escape já fazia aqui no teclado. Some a tela inteira em vez de
 * desfazer um passo.
 *
 * O segundo: **sair sem perguntar**. Na raiz, um toque distraído encerra o app. O padrão do Android
 * é confirmar, e aqui vale mais do que o normal: quem está na busca com filtros e data escolhidos
 * perde tudo.
 *
 * <h2>A ordem</h2>
 * Overlay → histórico → confirmar saída. É a mesma escala do Escape: desfazer o menor passo
 * possível antes de desfazer o maior.
 *
 * A confirmação é um `confirm` do sistema de propósito, e é a única exceção à regra do projeto de
 * não usar diálogo nativo (ver a nota em pages/Agenda.jsx). Aqui a pergunta é do sistema
 * operacional — "encerrar este aplicativo?" —, não do produto, e o `backButton` do Capacitor é
 * síncrono: um diálogo React responderia depois de o app já ter fechado.
 */
export function useBotaoVoltarNativo() {
	const navigate = useNavigate();
	const location = useLocation();

	// A rota por ref: assinar `location` no efeito reinscreveria o listener nativo a cada navegação
	// do app inteiro, e o registro do plugin é assíncrono — duas inscrições em corrida deixariam
	// listener órfão.
	const localizacaoRef = useRef(location);
	localizacaoRef.current = location;

	useEffect(() => {
		if (!ehNativo()) return undefined;

		let remover;
		let cancelado = false;

		(async () => {
			const { App } = await import('@capacitor/app');

			const inscricao = await App.addListener('backButton', ({ canGoBack }) => {
				// 1. Overlay aberto: fecha o do topo e para por aqui.
				if (fecharOverlayDoTopo()) return;

				// 2. Dá para voltar no histórico do app: volta.
				//    `canGoBack` é a leitura do plugin sobre o histórico da WebView; a raiz do site
				//    também conta como fim de linha, porque voltar de "/" só sairia do app.
				//
				//    Desde o RaizDoApp (App.jsx), "/" não chega a ser a rota atual dentro do app: ela
				//    desvia no primeiro render para /login (sem sessão) ou /dashboard (com sessão), e
				//    é uma dessas duas que passou a ser o fim de linha aqui — é nelas que a pergunta
				//    do passo 3 aparece. A comparação com "/" fica como rede de segurança: o desvio é
				//    de uma rota, e uma rota pode voltar a renderizar aqui.
				if (canGoBack && localizacaoRef.current.pathname !== '/') {
					navigate(-1);
					return;
				}

				// 3. Fim de linha: pergunta antes de encerrar.
				// eslint-disable-next-line no-restricted-globals
				if (window.confirm('Sair do Nova Faxina?')) App.exitApp();
			});

			if (cancelado) inscricao.remove();
			else remover = () => inscricao.remove();
		})();

		return () => {
			cancelado = true;
			remover?.();
		};
	}, [navigate]);
}

export default useBotaoVoltarNativo;
